/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useDebouncedCallback,
} from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';

/**
 * Internal dependencies
 */
import { PROVIDERS } from '../../../../../app/media/media3p/providerConfiguration';
import MediaGallery from './mediaGallery';
import useThumbnailLoadGate from './useThumbnailLoadGate';
import {
  MediaGalleryContainer,
  MediaGalleryInnerContainer,
  MediaGalleryMessage,
} from './styles';
import MediaError from './mediaError';
import LoadingPill, { useShowLoadingPill } from '../../common/loadingPill';

const ROOT_MARGIN = 300;

/**
 * PaginatedMediaGallery component with scroll-based pagination.
 *
 * IMPORTANT: This component requires a scrollable parent container for pagination to work correctly.
 * MediaGalleryContainer has overflow:visible to allow folder navigation UI to display properly,
 * so scroll calculations must be performed on the actual scrollable ancestor (e.g., ScrollableContent
 * in media3p pane). Pass the scrollContainerRef prop to enable proper pagination behavior.
 *
 * Without scrollContainerRef, pagination calculations fall back to the local container ref,
 * which may cause incorrect auto-triggering of pagination when overflow:visible is used.
 */
const GalleryViewport = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
`;

const MediaGalleryLoadingPillContainer = styled.div`
  position: sticky;
  bottom: 0;
  width: 100%;
  display: block;
`;

function PaginatedMediaGallery({
  folder,
  providerType,
  resources,
  player,
  searchTerm,
  selectedCategoryId,
  tag,
  isMediaLoading,
  isMediaLoaded,
  hasMore,
  onDimensionsUpdate,
  onInsert,
  setNextPage,
  canEditMedia,
  hasError,
  scrollContainerRef, // Parent scrollable container ref for pagination calculations
}) {
  // Local ref for MediaGalleryContainer (for scrollTo operations)
  const refContainer = useRef();
  const showLoadingPill = useShowLoadingPill(isMediaLoading);
  const disableLocalScroll = Boolean(scrollContainerRef);
  const resourceIds = useMemo(
    () => resources.map((resource) => resource.id),
    [resources]
  );
  const resetKey = [
    providerType,
    searchTerm,
    selectedCategoryId,
    folder,
    tag,
    player,
  ].join('|');
  const { isBatchReady, onThumbnailReady } = useThumbnailLoadGate({
    resourceIds,
    resetKey,
  });

  const loadNextPageIfNeeded = useCallback(() => {
    const node = scrollContainerRef?.current || refContainer.current;
    if (
      !resources.length ||
      !node ||
      // This condition happens when the component is hidden, and causes the
      // calculation below to load a new page in error.
      node.clientHeight === 0 ||
      !hasMore ||
      !isMediaLoaded ||
      isMediaLoading ||
      hasError
    ) {
      return;
    }

    // Load the next page if we are "close" (by a length of ROOT_MARGIN) to the
    // bottom of of the container.
    const bottom =
      node.scrollHeight - node.scrollTop <= node.clientHeight + ROOT_MARGIN;
    if (bottom) {
      setNextPage();
      return;
    }

    // Load the next page if the page isn't full, ie. scrollbar is not visible.
    if (node.clientHeight === node.scrollHeight) {
      setNextPage();
      return;
    }
  }, [
    resources,
    scrollContainerRef,
    hasMore,
    isMediaLoaded,
    isMediaLoading,
    setNextPage,
    hasError,
  ]);

  // Scroll to the top when the searchTerm or selected category changes.
  // Use the parent scrollable container if provided, otherwise use local ref
  useEffect(() => {
    const scrollNode = scrollContainerRef?.current || refContainer.current;
    scrollNode?.scrollTo(0, 0);
  }, [folder, player, searchTerm, selectedCategoryId, tag, scrollContainerRef]);

  // After scroll or resize, see if we need the load the next page.
  const handleScrollOrResize = useDebouncedCallback(loadNextPageIfNeeded, 500);

  // After loading a next page, see if we need to load another,
  // ie. when the page of results isn't full.
  useLayoutEffect(() => {
    const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

    async function loadNextPageIfNeededAfterGalleryRendering() {
      // Wait for <Gallery> to finish its render layout cycles first.
      await sleep(200);

      loadNextPageIfNeeded();
    }
    loadNextPageIfNeededAfterGalleryRendering();
  }, [loadNextPageIfNeeded]);

  useEffect(() => {
    // Attach scroll listener to the parent scrollable container if provided
    const node = scrollContainerRef?.current || refContainer.current;
    if (!node) {
      return undefined;
    }

    // When the user scrolls or resizes (debounced).
    node.addEventListener('scroll', handleScrollOrResize);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      node.removeEventListener('scroll', handleScrollOrResize);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [handleScrollOrResize, scrollContainerRef]);

  const mediaGallery =
    isMediaLoaded && resources.length === 0 ? (
      <MediaGalleryMessage>
        {__('No media found.', 'web-stories')}
      </MediaGalleryMessage>
    ) : (
      <div style={{ marginBottom: 15 }}>
        <MediaGallery
          providerType={providerType}
          canEditMedia={canEditMedia}
          resources={resources}
          onDimensionsUpdate={onDimensionsUpdate}
          onInsert={onInsert}
          isThumbnailBatchReady={isBatchReady}
          onThumbnailReady={onThumbnailReady}
        />
      </div>
    );

  const attribution =
    providerType !== 'local' &&
    PROVIDERS[providerType].attributionComponent &&
    PROVIDERS[providerType].attributionComponent();

  return (
    <GalleryViewport>
      <MediaGalleryContainer
        data-testid="media-gallery-container"
        ref={refContainer}
        isEmpty={resources.length === 0}
        disableScroll={disableLocalScroll}
      >
        <MediaGalleryInnerContainer>
          {hasError ? (
            <MediaError onRetry={() => setNextPage()} />
          ) : (
            mediaGallery
          )}
        </MediaGalleryInnerContainer>
      </MediaGalleryContainer>

      <MediaGalleryLoadingPillContainer>
        {showLoadingPill && <LoadingPill />}
        {!showLoadingPill && attribution}
      </MediaGalleryLoadingPillContainer>
    </GalleryViewport>
  );
}

PaginatedMediaGallery.propTypes = {
  providerType: PropTypes.string.isRequired,
  resources: PropTypes.arrayOf(PropTypes.object).isRequired,
  isMediaLoading: PropTypes.bool.isRequired,
  isMediaLoaded: PropTypes.bool.isRequired,
  hasMore: PropTypes.bool.isRequired,
  onInsert: PropTypes.func.isRequired,
  setNextPage: PropTypes.func.isRequired,
  player: PropTypes.string,
  searchTerm: PropTypes.string,
  selectedCategoryId: PropTypes.string,
  canEditMedia: PropTypes.bool,
  scrollContainerRef: PropTypes.object, // Ref to parent scrollable container
};

PaginatedMediaGallery.defaultProps = {
  canEditMedia: false,
};

export default PaginatedMediaGallery;
